import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Building2, Plus, Users, FileText, AlertTriangle, Bell, CheckCircle2, Edit2, Upload, Eye, Pencil, Trash2, Phone, Mail, MapPin, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const EntreprisesManager = () => {
  const { user } = useAuth();
  const [entreprises, setEntreprises] = useState([]);
  const [contrats, setContrats] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEntreprise, setShowAddEntreprise] = useState(false);
  const [showEditEntreprise, setShowEditEntreprise] = useState(false);
  const [selectedEntreprise, setSelectedEntreprise] = useState(null);
  const [editingEntreprise, setEditingEntreprise] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [newEntreprise, setNewEntreprise] = useState({
    nom: '',
    adresse: '',
    telephone: '',
    email: '',
    secteur: '',
    zone: '',
    contact_nom: '',
    contact_prenom: '',
    contact_telephone: '',
    contact_email: '',
    contact_fonction: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [entreprisesRes, contratsRes, alertesRes, beneficiairesRes] = await Promise.all([
        api.get('/entreprises'),
        api.get('/contrats'),
        api.get('/alertes-suivi'),
        api.get('/apprenants')
      ]);
      setEntreprises(entreprisesRes.data);
      setContrats(contratsRes.data);
      setAlertes(alertesRes.data);
      setBeneficiaires(beneficiairesRes.data);
    } catch (error) {
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntreprise = async () => {
    try {
      await api.post('/entreprises', newEntreprise);
      toast.success('Entreprise ajoutée avec succès');
      setShowAddEntreprise(false);
      setNewEntreprise({ nom: '', adresse: '', telephone: '', email: '', secteur: '', zone: '', contact_nom: '', contact_prenom: '', contact_telephone: '', contact_email: '', contact_fonction: '' });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleEditEntreprise = (entreprise) => {
    setEditingEntreprise({ ...entreprise });
    setShowEditEntreprise(true);
  };

  const handleUpdateEntreprise = async () => {
    try {
      await api.put(`/entreprises/${editingEntreprise.id}`, editingEntreprise);
      toast.success('Entreprise mise à jour avec succès');
      setShowEditEntreprise(false);
      setEditingEntreprise(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteEntreprise = async (entrepriseId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette entreprise ?')) return;
    try {
      await api.delete(`/entreprises/${entrepriseId}`);
      toast.success('Entreprise supprimée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getBeneficiaireName = (id) => {
    const b = beneficiaires.find(b => b.id === id);
    return b ? `${b.prenom} ${b.nom}` : 'Inconnu';
  };

  const alertesPendantes = alertes.filter(a => !a.effectuee);
  const alertesUrgentes = alertesPendantes.filter(a => {
    const datePrevue = new Date(a.date_prevue);
    const today = new Date();
    const diffDays = Math.ceil((datePrevue - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  });

  const filteredEntreprises = entreprises.filter(e => 
    e.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.secteur?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.zone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement des entreprises...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-3xl shadow-sm border-0">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                <Building2 className="w-7 h-7 text-amber-600" />
                Base de données Entreprises
              </h2>
              <p className="text-slate-600 mt-1">
                {entreprises.length} entreprise(s) partenaire(s)
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64 rounded-xl"
              />
              <Button
                onClick={() => setShowAddEntreprise(true)}
                className="bg-amber-600 hover:bg-amber-700 rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Entreprise
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alertes urgentes */}
      {alertesUrgentes.length > 0 && (
        <Card className="bg-amber-50 border-amber-200 rounded-3xl shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
            <div>
              <p className="font-bold text-amber-900">
                {alertesUrgentes.length} alerte(s) de suivi urgente(s)
              </p>
              <p className="text-sm text-amber-700">
                Des suivis post-embauche sont prévus dans les 7 prochains jours
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des entreprises */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEntreprises.map(entreprise => {
          const entrepriseContrats = contrats.filter(c => c.entreprise_id === entreprise.id);
          return (
            <Card key={entreprise.id} className="bg-white rounded-3xl shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-bold text-slate-900">{entreprise.nom}</CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className="bg-amber-100 text-amber-700">{entreprise.secteur}</Badge>
                      <Badge variant="outline">{entreprise.zone}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditEntreprise(entreprise)}
                      className="text-blue-600 hover:bg-blue-50"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteEntreprise(entreprise.id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-2 text-sm text-slate-600">
                  {entreprise.adresse && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{entreprise.adresse}</span>
                    </div>
                  )}
                  {entreprise.telephone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{entreprise.telephone}</span>
                    </div>
                  )}
                  {entreprise.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span>{entreprise.email}</span>
                    </div>
                  )}
                  {entreprise.contact_nom && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span>{entreprise.contact_prenom} {entreprise.contact_nom} ({entreprise.contact_fonction})</span>
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Contrats actifs</span>
                    <Badge className="bg-blue-100 text-blue-700">{entrepriseContrats.length}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredEntreprises.length === 0 && (
        <Card className="bg-white rounded-3xl shadow-sm">
          <CardContent className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucune entreprise</h3>
            <p className="text-slate-500 mb-4">
              {searchTerm ? 'Aucune entreprise ne correspond à votre recherche.' : 'Ajoutez votre première entreprise partenaire.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Dialog Ajouter Entreprise */}
      <Dialog open={showAddEntreprise} onOpenChange={setShowAddEntreprise}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-600" />
              Nouvelle entreprise
            </DialogTitle>
            <DialogDescription>
              Ajoutez une nouvelle entreprise partenaire à la base de données
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700">Nom de l'entreprise *</label>
                <Input
                  value={newEntreprise.nom}
                  onChange={(e) => setNewEntreprise({ ...newEntreprise, nom: e.target.value })}
                  placeholder="Ex: SARL Dupont"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Secteur *</label>
                <Select
                  value={newEntreprise.secteur}
                  onValueChange={(val) => setNewEntreprise({ ...newEntreprise, secteur: val })}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BTP">BTP</SelectItem>
                    <SelectItem value="Commerce">Commerce</SelectItem>
                    <SelectItem value="Restauration">Restauration</SelectItem>
                    <SelectItem value="Services">Services</SelectItem>
                    <SelectItem value="Industrie">Industrie</SelectItem>
                    <SelectItem value="Agriculture">Agriculture</SelectItem>
                    <SelectItem value="Transport">Transport</SelectItem>
                    <SelectItem value="Santé">Santé</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Zone *</label>
                <Select
                  value={newEntreprise.zone}
                  onValueChange={(val) => setNewEntreprise({ ...newEntreprise, zone: val })}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
                    <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700">Adresse</label>
                <Input
                  value={newEntreprise.adresse}
                  onChange={(e) => setNewEntreprise({ ...newEntreprise, adresse: e.target.value })}
                  placeholder="Adresse complète"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Téléphone</label>
                <Input
                  value={newEntreprise.telephone}
                  onChange={(e) => setNewEntreprise({ ...newEntreprise, telephone: e.target.value })}
                  placeholder="0262 XX XX XX"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Email</label>
                <Input
                  type="email"
                  value={newEntreprise.email}
                  onChange={(e) => setNewEntreprise({ ...newEntreprise, email: e.target.value })}
                  placeholder="contact@entreprise.re"
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Contact principal</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Prénom</label>
                  <Input
                    value={newEntreprise.contact_prenom}
                    onChange={(e) => setNewEntreprise({ ...newEntreprise, contact_prenom: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Nom</label>
                  <Input
                    value={newEntreprise.contact_nom}
                    onChange={(e) => setNewEntreprise({ ...newEntreprise, contact_nom: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Fonction</label>
                  <Input
                    value={newEntreprise.contact_fonction}
                    onChange={(e) => setNewEntreprise({ ...newEntreprise, contact_fonction: e.target.value })}
                    placeholder="Ex: DRH"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Téléphone</label>
                  <Input
                    value={newEntreprise.contact_telephone}
                    onChange={(e) => setNewEntreprise({ ...newEntreprise, contact_telephone: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEntreprise(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button 
              onClick={handleCreateEntreprise}
              disabled={!newEntreprise.nom || !newEntreprise.secteur || !newEntreprise.zone}
              className="bg-amber-600 hover:bg-amber-700 rounded-xl"
            >
              Créer l'entreprise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Éditer Entreprise */}
      <Dialog open={showEditEntreprise} onOpenChange={setShowEditEntreprise}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              Modifier l'entreprise
            </DialogTitle>
          </DialogHeader>
          {editingEntreprise && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium text-slate-700">Nom *</label>
                  <Input
                    value={editingEntreprise.nom}
                    onChange={(e) => setEditingEntreprise({ ...editingEntreprise, nom: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Secteur *</label>
                  <Select
                    value={editingEntreprise.secteur}
                    onValueChange={(val) => setEditingEntreprise({ ...editingEntreprise, secteur: val })}
                  >
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BTP">BTP</SelectItem>
                      <SelectItem value="Commerce">Commerce</SelectItem>
                      <SelectItem value="Restauration">Restauration</SelectItem>
                      <SelectItem value="Services">Services</SelectItem>
                      <SelectItem value="Industrie">Industrie</SelectItem>
                      <SelectItem value="Agriculture">Agriculture</SelectItem>
                      <SelectItem value="Transport">Transport</SelectItem>
                      <SelectItem value="Santé">Santé</SelectItem>
                      <SelectItem value="Autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Zone *</label>
                  <Select
                    value={editingEntreprise.zone}
                    onValueChange={(val) => setEditingEntreprise({ ...editingEntreprise, zone: val })}
                  >
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
                      <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-slate-700">Adresse</label>
                  <Input
                    value={editingEntreprise.adresse || ''}
                    onChange={(e) => setEditingEntreprise({ ...editingEntreprise, adresse: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Téléphone</label>
                  <Input
                    value={editingEntreprise.telephone || ''}
                    onChange={(e) => setEditingEntreprise({ ...editingEntreprise, telephone: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Email</label>
                  <Input
                    type="email"
                    value={editingEntreprise.email || ''}
                    onChange={(e) => setEditingEntreprise({ ...editingEntreprise, email: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditEntreprise(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button onClick={handleUpdateEntreprise} className="bg-blue-600 hover:bg-blue-700 rounded-xl">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EntreprisesManager;
